/**
 * 通知の「種類」(カテゴリ) — 通知ドロワーのリデザイン (notification-drawer-redesign、2026-09-06)。
 *
 * ドロワーの 2 行目「書類提出 · 自己破産（管財）/ 書類回収」と、種類の絞り込み select に使う。
 * BE `NotificationType` (23 値) を人が読める 8 区分へ畳む。
 *
 * ## 契約
 * - **未マップ値でも落ちない** (CLAUDE.md §2.4): 未知の type は `other` (その他) に倒す
 * - `NotificationType` に値が増えたら `CATEGORY_BY_TYPE` が型エラーで知らせる (網羅性チェック)
 * - 表示文言は辞書 (`i18n/locales/{ja,en}/notifications.ts` の `categories` / `surfaces`) にあり、
 *   日本語の表 (`NOTIFICATION_CATEGORY_LABELS`) も辞書から作る。ドロワーは文字列を持たない
 * - 表示言語は省略可能な `locale` 引数 (既定は日本語)
 */
import type { Notification, NotificationType } from '@/types';
import { caseTypeLabel } from '@/constants/caseTypes';
import type { Locale } from '@/i18n';
import { localizedCaseTitle, localizedCaseTypeLabel } from '@/i18n/enums/case';
import { notificationsJa } from '@/i18n/locales/ja/notifications';
import { translate } from '@/i18n/translate';
// 名前空間 `notifications` を登録する (英語の種類名・面の名前を引く)
import '@/i18n/namespaces/notifications';

export type NotificationCategory =
  | 'document' // 書類提出 (依頼者側のアクション)
  | 'review' // 書類確認 (事務所側の確認 / 差し戻し)
  | 'message' // メッセージ (ポータル / Chatwork / LINE / 依頼者ダイレクト)
  | 'guide' // 案内・リマインド (催促メール / URL 共有 / パスワード通知 / 家計表 URL)
  | 'email' // メール配信 (不達 / 迷惑メール申告)
  | 'case' // 案件 (受任による自動立ち上げ)
  | 'task' // タスク (担当割り当て / 引き継ぎ)
  | 'other';

/** 種類の表示ラベル (日本語)。辞書 `notifications.categories` と同じ。 */
export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> =
  notificationsJa.categories;

/** 種類の表示ラベル (表示言語別)。 */
export function notificationCategoryName(
  category: NotificationCategory,
  locale: Locale = 'ja',
): string {
  return translate(locale, `notifications.categories.${category}`);
}

/** 種類の絞り込み select の選択肢 (表示順)。「すべての種類」は呼び出し側が先頭に置く。 */
export const NOTIFICATION_CATEGORY_FILTER_OPTIONS: {
  value: NotificationCategory;
  label: string;
}[] = (['document', 'review', 'message', 'guide', 'email', 'case', 'task', 'other'] as const).map(
  (value) => ({ value, label: NOTIFICATION_CATEGORY_LABELS[value] }),
);

const CATEGORY_BY_TYPE: Record<NotificationType, NotificationCategory> = {
  'item.uploaded': 'document',
  'item.resubmitted': 'document',
  'collection.requester_item_added': 'document',
  'collection.requester_item_deferred': 'document',
  'collection.requester_extension_requested': 'document',
  'collection.completed': 'document',
  'item.confirmed': 'review',
  'item.rejected': 'review',
  'portal.message_received': 'message',
  'portal.message_sent': 'message',
  'cw.message_received': 'message',
  'cw.message_sent': 'message',
  'line.message_received': 'message',
  'line.message_sent': 'message',
  'client.message_received': 'message',
  'collection.reminded': 'guide',
  'collection.url_shared': 'guide',
  'collection.password_notified': 'guide',
  'budget.url_shared': 'guide',
  'email.bounced': 'email',
  'email.complained': 'email',
  'client.email_undeliverable': 'email',
  // steering 649: 外部サービスのフォーム回答を対話記録に残した通知。
  // 依頼者とのやりとりの記録なので message 扱いにする (書類でも案件でもない)。
  'inbound.form_response': 'message',
  'case.accepted': 'case',
  'task.follow_up': 'task',
  'case.patrol_daily': 'case',
  'task.assigned': 'task',
  'task.reassigned': 'task',
};

/** type → カテゴリ。未知 / 空は `other`。 */
export function notificationCategory(type: string | null | undefined): NotificationCategory {
  const key = typeof type === 'string' ? type.trim() : '';
  return (CATEGORY_BY_TYPE as Record<string, NotificationCategory | undefined>)[key] ?? 'other';
}

/** カテゴリの表示ラベル (未知値は「その他」)。 */
export function notificationCategoryLabel(
  type: string | null | undefined,
  locale: Locale = 'ja',
): string {
  return notificationCategoryName(notificationCategory(type), locale);
}

/** 面の辞書キー (`notifications.surfaces.*`)。面を出さない種類は null。 */
type SurfaceKey = keyof typeof notificationsJa.surfaces;

function notificationSurfaceKey(category: NotificationCategory): SurfaceKey | null {
  switch (category) {
    case 'document':
    case 'review':
    case 'guide':
      return 'collection';
    case 'message':
      return 'message';
    case 'email':
      return 'email';
    case 'case':
      return 'case';
    case 'task':
      return 'task';
    default:
      return null;
  }
}

/**
 * パンくずの末尾 = その出来事が起きた面。空文字なら面を出さない。
 * (書類系はすべて「書類回収」の面で起きる。案件詳細のチェックリスト / 対応履歴タブの呼び名ではなく、
 *  依頼者から見た機能名で揃える)
 */
export function notificationSurfaceLabel(
  category: NotificationCategory,
  locale: Locale = 'ja',
): string {
  const key = notificationSurfaceKey(category);
  return key ? translate(locale, `notifications.surfaces.${key}`) : '';
}

/**
 * ドロワー 2 行目のパンくず: `書類提出 · 自己破産（管財） / 書類回収`。
 *
 * 事件種別の出どころ (優先順):
 * 1. `caseType` (BE が collection / case から写した raw enum、2026-09-06 追補) → `caseTypeLabel` で日本語に
 * 2. 無ければ `caseName` (legacy)。コレクション名がそのまま入っているレコード (`架空 太郎 自己破産(管財) 書類回収`)
 *    では、先頭の依頼者名 (1 行目に別で出す) を落とし、面の名前が既に含まれていれば面を重ねない
 * 3. どちらも無ければ `書類提出 · 書類回収` (案件名の欄を作らない)
 *
 * 文字列の整形はここ (表示専用) だけで、遷移先の判定には一切使わない。
 * 英語表示 (`locale='en'`) では種類・事件種別・面を英語にし、legacy の `caseName` (データ) はそのまま出す
 * (既定の案件名「個人再生事件」などと完全一致するときだけ `localizedCaseTitle` で英語にする)。
 * 「面の名前が既に含まれているか」は legacy の日本語の文字列と日本語の面の名前で比べる。
 */
export function notificationBreadcrumb(
  n: Pick<Notification, 'type'> & {
    caseName?: string | null;
    caseType?: string | null;
    clientName?: string | null;
  },
  locale: Locale = 'ja',
  resolve?: (value: string | null | undefined, fallback?: string) => string,
): string {
  const category = notificationCategory(n.type);
  const label = notificationCategoryName(category, locale);
  const surfaceJa = notificationSurfaceLabel(category);
  const surface = notificationSurfaceLabel(category, locale);

  const typed = resolve
    ? resolve(n.caseType, '')
    : caseTypeLabel(n.caseType, '')
      ? localizedCaseTypeLabel(locale, n.caseType)
      : '';
  let middle = typed;
  if (!middle) {
    let legacy = typeof n.caseName === 'string' ? n.caseName.trim() : '';
    const client = typeof n.clientName === 'string' ? n.clientName.trim() : '';
    if (client && legacy.startsWith(client)) legacy = legacy.slice(client.length).trim();
    // 既定の案件名 (「個人再生事件」など、事件種別 + 事件と完全一致) だけ英語表示で訳す。
    // それ以外の legacy (コレクション名など) はデータとしてそのまま。
    middle = localizedCaseTitle(locale, legacy);
  }
  const showSurface = surface && !(middle && middle.includes(surfaceJa));
  const tail = [middle, showSurface ? surface : ''].filter(Boolean).join(' / ');
  return tail ? `${label} · ${tail}` : label;
}
